import React from "react";
import { motion } from "framer-motion";
import ColyMascot from "@/components/ColyMascot";
import Wordmark from "@/components/Wordmark";

const pillars = [
  {
    emoji: "🤗",
    title: "Gentle, never preachy",
    body: "No judgement, no lectures. I cheer on calm days and hold space on tough ones.",
  },
  {
    emoji: "🎮",
    title: "Tracking, but make it fun",
    body: "Streaks, diamonds, levels. Logging finally feels like a tiny daily win, not a chore.",
  },
  {
    emoji: "🩺",
    title: "Your doctor's new best friend",
    body: "One-tap PDF reports turn months of data into a clear story for your next appointment.",
  },
];

const Welcome = () => {
  return (
    <section
      id="welcome"
      data-testid="welcome-section"
      className="relative px-5 sm:px-6 py-16 sm:py-24"
    >
      <div className="max-w-[1180px] mx-auto">
        {/* Letter card */}
        <div
          className="relative rounded-[28px] sm:rounded-[36px] border border-coly-ink/10 bg-white p-6 sm:p-12 lg:p-16 overflow-hidden"
          style={{
            boxShadow: "0 1px 0 rgba(47,58,44,0.05), 0 30px 80px -40px rgba(47,58,44,0.25)",
          }}
        >
          {/* corner Coly */}
          <motion.div
            initial={{ rotate: -6, y: 20, opacity: 0 }}
            whileInView={{ rotate: -6, y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ type: "spring", stiffness: 80, damping: 14, delay: 0.1 }}
            className="absolute -top-3 -right-3 sm:-top-6 sm:-right-6 hidden sm:block"
          >
            <ColyMascot size={140} mood="streak" withSparkles className="-rotate-6" />
          </motion.div>

          {/* tape decoration */}
          <div
            className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 rounded-sm rotate-2 hidden sm:block"
            style={{ background: "rgba(244,201,93,0.45)", border: "1px dashed rgba(47,58,44,0.15)" }}
          />

          <div className="relative grid lg:grid-cols-[1fr_1.8fr] gap-8 lg:gap-12 items-start">
            {/* mobile + tablet inline mascot */}
            <div className="flex justify-center sm:hidden -mt-2 mb-2">
              <ColyMascot size={120} mood="streak" withSparkles />
            </div>

            {/* left: avatar + signature on desktop */}
            <div className="hidden lg:flex flex-col items-start pt-2">
              <ColyMascot size={170} mood="streak" withSparkles className="-rotate-6" />
              <div className="mt-5">
                <div
                  className="text-[34px] text-coly-green-deep leading-none"
                  style={{
                    fontFamily: "'Caveat', 'Bagel Fat One', cursive",
                    transform: "rotate(-4deg)",
                  }}
                >
                  — Coly
                </div>
                <div className="text-[12px] font-bold text-coly-ink-soft mt-1 ml-1">
                  Your tiny gut buddy
                </div>
              </div>
            </div>

            {/* right: letter */}
            <div className="text-center lg:text-left">
              <div className="inline-block text-[11px] font-extrabold tracking-[1.6px] text-coly-yellow-deep uppercase mb-3">
                A note from Coly
              </div>
              <h2 className="text-[30px] sm:text-[40px] lg:text-[48px] font-black m-0 leading-[1.05] tracking-[-1px]">
                Hi there <span className="inline-block animate-wiggle origin-bottom-right">👋</span>
                <br />
                I'm <Wordmark inline color="#3F7556" />.
              </h2>
              <div className="mt-6 space-y-4 text-[16px] sm:text-[17px] leading-[1.6] text-coly-ink-soft font-medium max-w-[640px] mx-auto lg:mx-0">
                <p>
                  Living with Crohn's, UC, or IBS isn't simple. Some days are a quiet win.
                  Some days are a full-on flare. Most days are somewhere in between, with you
                  trying to remember what you ate three Tuesdays ago.
                </p>
                <p>
                  I'm here to make all of that{" "}
                  <span className="bg-coly-yellow/40 px-1.5 rounded font-bold text-coly-ink">
                    softer
                  </span>{" "}
                  — a friend for your gut. I'll log with you, learn your patterns, and
                  cheer you on. No noise, no nagging, no medical lectures.
                </p>
                <p className="font-bold text-coly-ink">
                  Welcome to the gut warrior club. We're so glad you're here.
                </p>
              </div>

              {/* mobile signature */}
              <div className="lg:hidden mt-6 flex items-center justify-center gap-2">
                <div
                  className="text-[28px] text-coly-green-deep leading-none"
                  style={{
                    fontFamily: "'Caveat', 'Bagel Fat One', cursive",
                    transform: "rotate(-4deg)",
                  }}
                >
                  — Coly
                </div>
                <div className="text-[11px] font-bold text-coly-ink-soft">
                  · Your tiny gut buddy
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3 pillars */}
        <div className="mt-8 sm:mt-10 grid md:grid-cols-3 gap-4 sm:gap-5">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              data-testid={`welcome-pillar-${i}`}
              className="bg-coly-surface-alt rounded-3xl p-6 border border-coly-ink/8 hover:-translate-y-1 transition-transform"
            >
              <div className="text-3xl mb-3">{p.emoji}</div>
              <h3 className="font-black text-[18px] tracking-tight m-0 mb-1.5">{p.title}</h3>
              <p className="text-[14px] leading-relaxed text-coly-ink-soft m-0 font-medium">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Welcome;
